/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { dirOfLang } from "../../Helper";
import { isFeatureOn } from "../../portalSettings";

/**
 * בורר השפה - **רכיב אחד, ולא עותק לכל מסך.**
 *
 * הוא היה בתוך `Header` בלבד, ולכן כל מסך שאינו מצויר בתוך `Frame` לא קיבל אותו:
 * מסך ההתחברות הסלולרי הוא כזה, ולקוח שהגיע אליו לא יכול היה לשנות שפה כלל.
 *
 * הבורר מוצהר בקטלוג כ**תצוגה בלבד** (`showLanguageSwitcher`). אין מסלול שרת שנחסם
 * כאן, וזה בסדר: השפה אינה הרשאה. הצהרה שקרית כ"נאכף" הייתה מלמדת לקרוא את שדה
 * ה-enforcement כקוסמטי גם כשהוא לא.
 *
 * הבחירה נשמרת ב-`localStorage` תחת `lang` - אותו מפתח ש-`getLang()` קורא, ולכן היא
 * גם השפה שנשלחת בכותרת `lang` בכל קריאה הלאה. שמירה בלבד, בלי רענון המסך: `i18n`
 * מצייר מחדש בעצמו.
 */
const LANGUAGES = [
  { lang: "en", flag: "/images/flags/flag-usa.png", label: "English" },
  { lang: "he", flag: "/images/flags/flag-israel.png", label: "עברית" },
];

/** `className` מאפשר למסך למקם את הבורר בלי שהרכיב יכיר את הפריסה שלו. */
const LanguageSwitcher = ({ className = "" }) => {
  const { i18n } = useTranslation();
  const reseller = useSelector((state) => state.reseller.value);

  if (!isFeatureOn(reseller, "showLanguageSwitcher")) {
    return null;
  }

  const changeLanguage = (lang) => {
    try {
      localStorage.setItem("lang", lang);
    } catch {
      // דפדפן שחוסם אחסון. השפה תחזיק למסך הזה, ותיגזר מהדפדפן בטעינה הבאה.
    }
    i18n.changeLanguage(lang);
    document.documentElement.setAttribute("dir", dirOfLang(lang));
  };

  return (
    <div className={`language-switcher ${className}`.trim()}>
      {LANGUAGES.map(({ lang, flag, label }) => (
        <img
          key={lang}
          src={flag}
          onClick={() => changeLanguage(lang)}
          className={`ml5 mr5 flag-icon${i18n.language === lang ? " flag-icon-active" : ""}`}
          alt={label}
          title={label}
        />
      ))}
    </div>
  );
};

export default LanguageSwitcher;
